import { useMemo } from 'react';
import {
  EventKind,
  EventType,
  EVENT_LABEL_MATCH,
  EVENT_STATE,
} from './constants';
import { Video } from '../MediaItemList';

/**
 * Returns TRUE if the given event is answered/completed
 * @param event - The event to evaluate
 */
export function isAnswered(event: Video): boolean {
  return event.state === EVENT_STATE.completed;
}

/**
 * Returns TRUE if the given event is an Alexa event
 * @param event - The event to evaluate
 */
export function isAlexa(event: Video): boolean {
  return !!event.properties?.is_alexa;
}

/**
 * Returns TRUE if person is detected in the given event
 * @param event - The event to evaluate
 */
export function isPersonDetected(event: Video): boolean {
  const cvProps = event.cv;
  return !!cvProps?.person_detected && cvProps?.detection_type === 'human';
}

/**
 * Returns TRUE if persistent vistor is detected in the given event
 * @param event - The event to evaluate
 */
export function isPersistentVisitor(event: Video): boolean {
  return !!(event.cv?.detection_type === 'loitering');
}

/**
 * Returns TRUE if the given event is a Quick Replies event
 * @param event - The event to evaluate
 */
export function isQuickReplies(event: Video): boolean {
  return !!event.properties?.is_autoreply;
}

/**
 * Returns TRUE if the given event is a "Package Delivered" event.
 * @param event - The event to evaluate
 */
export function isPackageDetected(event: Video): boolean {
  return (
    event.type === EventKind.motion &&
    event.cv?.detection_type === 'package_delivery'
  );
}

/**
 * Returns TRUE if the given event is a "Custom Event Detected" event.
 * @param event - The event to evaluate
 */
export function isCustomEvent(event: Video): boolean {
  return (
    event.type === EventKind.motion &&
    event.cv?.detection_type === 'cv_triggers'
  );
}

/**
 * Returns TRUE if given event is from a drone
 * @param event - The event to evaluate
 */
export function isFlight(event: Video): boolean {
  return event.deviceType === 'always_home_camera';
}

export const buildEventLabel = (video: Video): string => {
  switch (video.type) {
    case EventKind.alarm:
      return EVENT_LABEL_MATCH[EventType.ALARM];

    case EventKind.ding:
      if (isAlexa(video)) return EVENT_LABEL_MATCH[EventType.ALEXA_GREETINGS];
      if (isQuickReplies(video))
        return EVENT_LABEL_MATCH[EventType.QUICK_REPLIES];
      else
        return isAnswered(video)
          ? EVENT_LABEL_MATCH[EventType.DING_ANSWERED]
          : EVENT_LABEL_MATCH[EventType.DING_MISSED];

    case EventKind.door_activity:
      return isAnswered(video)
        ? EVENT_LABEL_MATCH[EventType.KNOCK_ANSWERED]
        : EVENT_LABEL_MATCH[EventType.KNOCK_MISSED];

    case EventKind.door_intrusion:
      return EVENT_LABEL_MATCH[EventType.DOOR_INTRUSION];

    case EventKind.motion:
      if (isPersistentVisitor(video))
        return EVENT_LABEL_MATCH[EventType.MOTION_PERSISTENT];
      if (isPersonDetected(video))
        return isAnswered(video)
          ? EVENT_LABEL_MATCH[EventType.MOTION_PERSON_ANSWERED]
          : EVENT_LABEL_MATCH[EventType.MOTION_PERSON];
      if (isPackageDetected(video))
        return isAnswered(video)
          ? EVENT_LABEL_MATCH[EventType.PACKAGE_DETECTED_ANSWERED]
          : EVENT_LABEL_MATCH[EventType.PACKAGE_DETECTED];
      if (isCustomEvent(video))
        return isAnswered(video)
          ? EVENT_LABEL_MATCH[EventType.CUSTOM_EVENT_DETECTED_ANSWERED]
          : EVENT_LABEL_MATCH[EventType.CUSTOM_EVENT_DETECTED];
      if (isAnswered(video))
        return EVENT_LABEL_MATCH[EventType.MOTION_ANSWERED];
      else return EVENT_LABEL_MATCH[EventType.MOTION];

    case EventKind.on_demand:
      if (isFlight(video)) return EVENT_LABEL_MATCH[EventType.ON_DEMAND_FLIGHT];
      return EVENT_LABEL_MATCH[EventType.ON_DEMAND];

    case EventKind.on_demand_link:
      if (isFlight(video))
        return EVENT_LABEL_MATCH[EventType.ON_DEMAND_FLIGHT_LINK];
      return EVENT_LABEL_MATCH[EventType.ON_DEMAND_LINK];

    case EventKind.key_access:
      return EVENT_LABEL_MATCH[EventType.KEY_ACCESS];

    default:
      return 'Unknown Event';
  }
};

export const useHistoryName = (video: Video) => {
  const name = useMemo(() => buildEventLabel(video), [video]);

  return name;
};
